import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useBeforeUnload, useBlocker } from 'react-router-dom';

interface BlockerOptions {
  onProceed?: () => void;
  onCancel?: () => void;
}

export function useBlockNavigation(
  shouldBlock: boolean, 
  onBlock: (proceed: () => void, cancel: () => void) => void,
  options: BlockerOptions = {}
) {
  const location = useLocation();
  const blocker = useBlocker(shouldBlock);
  const pendingBlockerRef = useRef<any>(null);
  
  // Block navigation with the browser's built-in beforeunload event
  useBeforeUnload(
    useCallback(
      (event) => {
        if (shouldBlock) {
          event.preventDefault();
          return '';
        }
      },
      [shouldBlock]
    )
  );

  // Store the current blocker reference when it changes to blocked state
  useEffect(() => {
    if (blocker.state === 'blocked') {
      pendingBlockerRef.current = blocker;
    }
  }, [blocker, blocker.state]);

  // For route changes within the app
  useEffect(() => {
    if (blocker.state === 'blocked') {
      // Don't block the same route
      if (blocker.location.pathname === location.pathname) {
        blocker.proceed();
        return;
      }

      // Define proceed and cancel functions to pass to the onBlock callback
      const proceed = () => {
        if (pendingBlockerRef.current) {
          pendingBlockerRef.current.proceed();
          pendingBlockerRef.current = null;
          if (options.onProceed) options.onProceed();
        }
      };

      const cancel = () => {
        if (pendingBlockerRef.current) {
          pendingBlockerRef.current.reset();
          pendingBlockerRef.current = null;
          if (options.onCancel) options.onCancel();
        }
      };

      // Call the onBlock callback which should show our dialog
      onBlock(proceed, cancel);
    }
  }, [blocker, location.pathname, onBlock, options]);

  // Return the proceed and reset functions so they can be called from outside
  return {
    proceed: () => {
      if (pendingBlockerRef.current) {
        pendingBlockerRef.current.proceed();
        pendingBlockerRef.current = null;
      }
    },
    cancel: () => {
      if (pendingBlockerRef.current) {
        pendingBlockerRef.current.reset();
        pendingBlockerRef.current = null;
      }
    }
  };
}